import type { AriaAttributes, MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import { isInAppHref } from '@src/routing/href';
import { POLICY_PATH, PRIVACY_POLICY_PATH } from '@src/routing/routes';
import './Link.css';
import { trackPremium, type PremiumClickContext } from '@src/utils/premiumAnalytics';

function isPolicyHref(href: string): boolean {
  const path = href.split(/[?#]/)[0];
  return path === POLICY_PATH || path === PRIVACY_POLICY_PATH;
}

export type LinkVariant = 'plain' | 'chip' | 'inline' | 'quiet';

export interface LinkProps {
  /** Optional so <Trans> can inject the labelled text. */
  children?: ReactNode;
  href?: string;
  variant?: LinkVariant;
  target?: string;
  rel?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  testId?: string;
  premiumContext?: PremiumClickContext;
  'aria-current'?: AriaAttributes['aria-current'];
}

function linkRole(variant: LinkVariant) {
  if (variant === 'inline') {
    return 'label/link-inline' as const;
  }
  if (variant === 'quiet') {
    return 'label/link-quiet' as const;
  }
  return 'label/nav' as const;
}

export function Link({
  children,
  href,
  variant = 'plain',
  target,
  rel,
  onClick,
  testId,
  premiumContext,
  'aria-current': ariaCurrent,
}: LinkProps) {
  const className = classes('link', `link--${variant}`);
  const role = linkRole(variant);

  if (href) {
    const separateTab = target === '_blank' || (target === undefined && isPolicyHref(href));
    const inApp = !separateTab && isInAppHref(href);
    return (
      <Typography
        as={inApp ? RouterLink : 'a'}
        role={role}
        className={className}
        aria-current={ariaCurrent}
        {...(inApp ? { to: href } : { href })}
        target={separateTab ? '_blank' : target}
        rel={rel ?? (separateTab ? 'noopener noreferrer' : undefined)}
        onClick={(event) => {
          event.stopPropagation();
          if (new URL(href, window.location.origin).pathname === '/premium') {
            trackPremium(premiumContext?.action === 'learn_more' ? 'premium_learn_more_click_v2' : 'premium_navigation_clicked_v2', { placement: testId ?? 'premium_link', action: 'open_premium_page', ...premiumContext });
          }
          onClick?.(event);
        }}
        {...testIdAttr(testId)}
      >
        {children}
      </Typography>
    );
  }

  return (
    <Typography
      as="button"
      role={role}
      className={className}
      type="button"
      onClick={onClick}
      {...testIdAttr(testId)}
    >
      {children}
    </Typography>
  );
}
